import { Fragment } from 'react'
import {
  bulkChanges,
  grantedLookup,
  groupPermissions,
  type PermissionChange,
  useAdminPermissions,
  useBulkUpdateRolePermissions,
  useUpdateRolePermission,
} from '../../../features/admin/use-admin-permissions'
import { AdminPage, adminError, Meta } from './admin-bits'
import { Empty, Inline, th } from './controls'
import { mono, Panel } from './ui'

const linkBtn =
  'text-[10px] normal-case tracking-normal text-[var(--color-accent)] hover:underline disabled:opacity-40 focus-visible:outline-1 focus-visible:outline-[var(--color-accent)]'
const cell = 'border-b border-[var(--color-border)] px-3'

export function SignalAdminPermissions() {
  const q = useAdminPermissions()
  const one = useUpdateRolePermission()
  const bulk = useBulkUpdateRolePermissions()
  const roles = q.data?.roles ?? []
  const groups = groupPermissions(q.data?.permissions ?? [])
  const granted = grantedLookup(roles)
  const err = one.error ?? bulk.error
  const apply = (changes: PermissionChange[]) => {
    if (changes.length) bulk.mutate({ changes })
  }
  return (
    <AdminPage
      page="permissions"
      title="Permissions"
      meta={
        <Meta>
          {roles.length} roles x {q.data?.permissions.length ?? 0} perms - rbac cache up to 300s
        </Meta>
      }
      loading={q.isLoading}
      error={!!q.error}
    >
      {err ? <Inline tone="bad">{adminError(err)} Change reverted.</Inline> : null}
      {groups.length === 0 ? (
        <Panel title="Matrix">
          <Empty>No permissions defined.</Empty>
        </Panel>
      ) : (
        <Panel title="Roles x permissions">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={`${th} sticky left-0 bg-[var(--color-surface)]`}>Permission</th>
                  {roles.map((r) => (
                    <th key={r.id} className={`${th} text-center`}>
                      {r.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => {
                  const ids = g.permissions.map((p) => p.id)
                  return (
                    <Fragment key={g.groupTag}>
                      <tr className="bg-[var(--color-surface-2)]">
                        <th
                          scope="colgroup"
                          className={`${cell} sticky left-0 h-6 bg-[var(--color-surface-2)] text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--color-muted)]`}
                        >
                          {g.groupTag}
                        </th>
                        {roles.map((r) => (
                          <td key={r.id} className={`${cell} h-6 text-center`}>
                            <span className="inline-flex gap-1">
                              <button
                                type="button"
                                className={linkBtn}
                                disabled={bulk.isPending}
                                aria-label={`Grant all ${g.groupTag} to ${r.name}`}
                                onClick={() => apply(bulkChanges(roles, r.id, ids, true))}
                              >
                                all
                              </button>
                              <span aria-hidden="true" className="text-[10px] text-[var(--color-muted)]">
                                /
                              </span>
                              <button
                                type="button"
                                className={linkBtn}
                                disabled={bulk.isPending}
                                aria-label={`Revoke all ${g.groupTag} from ${r.name}`}
                                onClick={() => apply(bulkChanges(roles, r.id, ids, false))}
                              >
                                none
                              </button>
                            </span>
                          </td>
                        ))}
                      </tr>
                      {g.permissions.map((p) => (
                        <tr key={p.id} className="group hover:bg-[var(--color-surface-2)]">
                          <td
                            className={`${cell} sticky left-0 h-7 bg-[var(--color-surface)] text-[12px] group-hover:bg-[var(--color-surface-2)]`}
                            style={mono}
                          >
                            {p.name}
                          </td>
                          {roles.map((r) => {
                            const on = granted.get(r.id)?.has(p.id) ?? false
                            return (
                              <td key={r.id} className={`${cell} h-7 text-center`}>
                                <input
                                  type="checkbox"
                                  className="size-3.5 accent-[var(--color-accent)]"
                                  aria-label={`${r.name}: ${p.name}`}
                                  checked={on}
                                  onChange={() =>
                                    one.mutate({ roleId: r.id, permissionId: p.id, action: on ? 'revoke' : 'grant' })
                                  }
                                />
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </AdminPage>
  )
}
